import React, { useRef } from "react";
import toast from "react-hot-toast";
import { Formik, Form, Field, ErrorMessage, FormikValues } from "formik";
import * as Yup from "yup";
import { Spin } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { SAssignAsset, SUnassignAsset } from "../../../apis/app.service";
import AppButton from "../../sharedcomponents/Buttons/AppButton/AppButton";
import { useAssetsPage } from "../../../store/AssetsProvider";
import { formatDate } from "../../../helpers/FormatDate";
import { IAssignPayload } from "../core/models";

const assignSchema = Yup.object({
  name: Yup.string().required().label("Name"),
  email: Yup.string().email().label("Email"),
});

const EMPTY_ASSIGN = {
  name: "",
  email: "",
  department: "",
  phone: "",
  note: "",
};

export default function AssignAsset() {
  const { asset, activeAsset } = useAssetsPage();
  const modalRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();

  const assetId = asset.data.id;
  const current = activeAsset?.assignedTo;
  const history = activeAsset?.assignmentHistory || [];

  const closeModal = () => modalRef.current?.click();

  const onSuccess = (res: { message: string }) => {
    queryClient.invalidateQueries({ queryKey: ["assets"] });
    toast.success(res.message, { duration: 3000, position: "top-right" });
    closeModal();
  };
  const onError = (err: Error) =>
    toast.error(err.message, { duration: 3000, position: "top-right" });

  const assignMutation = useMutation({
    mutationFn: SAssignAsset,
    onSuccess,
    onError,
  });
  const unassignMutation = useMutation({
    mutationFn: SUnassignAsset,
    onSuccess,
    onError,
  });

  const submitForm = (values: IAssignPayload, _actions: FormikValues) => {
    if (!assetId) return;
    assignMutation.mutate({ assetId, body: values });
  };

  const isPending = assignMutation.isPending || unassignMutation.isPending;

  return (
    <div className="modal fade" id="kt_modal_assign_asset" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "760px" }}>
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="asset-modal-title">
              Assign Asset
              <small className="text-muted">{asset.data.assetNo}</small>
            </h2>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              ref={modalRef}
            ></button>
          </div>

          <div className="modal-body">
            {current?.name && (
              <div className="asset-current-holder mb-3">
                <span className="asset-current-holder__label">
                  Currently held by
                </span>
                <div className="asset-current-holder__body">
                  <div className="asset-current-holder__info">
                    <strong>{current.name}</strong>
                    {current.department ? ` · ${current.department}` : ""}
                    {current.assignedAt ? (
                      <small> (since {formatDate(current.assignedAt)})</small>
                    ) : null}
                  </div>
                  <AppButton
                    btnType="secondary"
                    width="130px"
                    disabled={isPending}
                    onClick={() => assetId && unassignMutation.mutate(assetId)}
                  >
                    Mark returned
                  </AppButton>
                </div>
              </div>
            )}

            <Formik
              validationSchema={assignSchema}
              initialValues={EMPTY_ASSIGN}
              onSubmit={submitForm}
              enableReinitialize
            >
              {() => (
                <Form className="form" noValidate>
                  <div className="row mb-3">
                    <div className="col-md-6 form-group">
                      <label className="form-label fs-5">
                        <span className="required">Assignee Name</span>
                      </label>
                      <Field type="text" className="form-control" name="name" />
                      <div className="text-danger">
                        <ErrorMessage name="name" />
                      </div>
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="form-label fs-5">Email</label>
                      <Field type="email" className="form-control" name="email" />
                      <div className="text-danger">
                        <ErrorMessage name="email" />
                      </div>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6 form-group">
                      <label className="form-label fs-5">Department</label>
                      <Field
                        type="text"
                        className="form-control"
                        name="department"
                      />
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="form-label fs-5">Phone</label>
                      <Field type="text" className="form-control" name="phone" />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-12 form-group">
                      <label className="form-label fs-5">Note</label>
                      <Field
                        type="text"
                        className="form-control"
                        name="note"
                        placeholder="e.g. Field work laptop"
                      />
                    </div>
                  </div>

                  <div className="mx-auto d-flex justify-content-center mt-2">
                    <AppButton btnType="success" type="submit" disabled={isPending}>
                      {isPending && <Spin size="small" className="me-2" />}
                      {current?.name ? "Reassign" : "Assign"}
                    </AppButton>
                  </div>
                </Form>
              )}
            </Formik>

            {history.length > 0 && (
              <div className="asset-history mt-4">
                <h4>Assignment History</h4>
                <ul>
                  {[...history].reverse().map((h, i) => (
                    <li key={i}>
                      <strong>{h.name}</strong>
                      {h.department ? ` · ${h.department}` : ""}
                      <span className="asset-history__dates">
                        {h.assignedAt ? formatDate(h.assignedAt) : ""}
                        {h.returnedAt
                          ? ` → returned ${formatDate(h.returnedAt)}`
                          : " → current"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
